import React, { useState, useEffect, useRef, useCallback, useMemo, useContext } from 'react';
import { makeStyles, useTheme } from '@mui/styles';

import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import AppBar from '@mui/material/AppBar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';

// import PdfIcon from '@mui/icons-material/PictureAsPdf';
import CsvIcon from '@mui/icons-material/TableChart';
import HtmlIcon from '@mui/icons-material/Code';
import TextIcon from '@mui/icons-material/Description';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CachedIcon from '@mui/icons-material/Cached';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import TuneIcon from '@mui/icons-material/Tune';
import ShareIcon from '@mui/icons-material/Share';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import WarningIcon from '@mui/icons-material/Warning';
import HuntIcon from '@mui/icons-material/TrackChanges';
import PollIcon from '@mui/icons-material/Poll';
import CloseIcon from '@mui/icons-material/Close';

import { get as _get } from 'lodash';
import { GridToolbarContainer, useGridApiContext } from '@mui/x-data-grid-pro';

import PopupState, { bindFocus } from 'material-ui-popup-state';

import PopupMenu, {
    CascadingMenu,
    CascadingLeftSubmenu,
    CascadingMenuItem
} from '../../../components/popupmenu';
import Tag from '../../../components/tags/tag';
import LabeledToolbar from '../../../components/styled/toolbar';
import { ExportTypes } from '../../../data/reports-service';
import Auth, { ProfileContext } from '../../../data/auth-service';
// import withWhyRender, { useWhatChangedEffect } from '../../../libraries/withwhyrender';

const { BRICATA_APPLIANCE_TYPE } = process.env;

const useStyles = makeStyles(theme => ({
    operator: {
        ...theme.typography.button,
        background: theme.palette.secondary.main,
        borderRadius: 5,
        display: 'inline',
        margin: 2,
        fontSize: 'small',
        padding: '3px 7px',
        width: 50
    },
    list: {
        display: 'flex',
        width: '100%',
        paddingTop: 6,
        paddingLeft: 8,
        paddingRight: 8,
        overflow: 'auto',
    },
    dropzone: {
        padding: 8,
        border: `1px dashed ${theme.palette.primary.main}`,
        width: '100%',
        minHeight: 32,

        '&.dragover': {
            border: `1px dashed ${theme.palette.secondary.main}`,
            cursor: 'copy',
        },

        '&.nodragover': {
            opacity: 0.5,
        },
    },
    toolbarContent: {
        padding: 8,
        verticalAlign: 'sub'
    },
    divider: {
        height: 40,
        marginTop: 10,
    },
    menuDivider: {
        height: 1,
        backgroundColor: theme.palette.common.black,
        marginLeft: 15,
        marginRight: 15,
        marginTop: 10,
        marginBottom: 10,
        opacity: 0.3,
    },
    closeButton: {
        position: 'relative',
        paddingLeft: 10,
        top: 5,
        // color: theme.palette.secondary.main,
    },
}));

const DEFAULT_BTNS = {
    columns: 0,
    groups: 0,
    tags: 0,
    tune: 0,
    attack: 0,
    incident: -1,
    alerts: -1,
    read: -1,
    report: -1,
    share: -1,
    fetch: -1,
    pause: -1,
    resume: -1,
};

// reduce render noise
const READ_LIST = ['read', 'unread', 'all'];

const ListMode = 'list';
const DetailMode = 'detail';
const HistoryMode = 'history';
const FivetupleMode = 'fivetuple';
const ThreetupleMode = 'threetuple';
const MapMode = 'map';

const TableToolbar = React.forwardRef((props, ref) => {
    const apiRef = useGridApiContext();
    const { state } = apiRef.current;
    const { updateProfile, getProfile } = useContext(ProfileContext);
    const tagPerms = Auth.permissions.get('Tags');
    const reportPerms = Auth.permissions.get('Reports');

    const classes = useStyles();
    const theme = useTheme();
    const dropzone = useRef(null);

    const [grouping, setGrouping] = useState(false);
    const [hideGrouping, setHideGrouping] = useState(false);
    const [groups, setGroups] = useState([]);
    const [mode, setMode] = useState();
    const [tags, setTags] = useState([]);
    const [tagsOp, setTagsOp] = useState();
    const [fields, setFields] = useState({});
    const [read, setRead] = useState();
    const [pause, setPause] = useState(false);
    const [hasAlerts, setHasAlerts] = useState(false);
    const [cmcs, setCmcs] = useState();
    const [selectedCmcs, setSelectedCmcs] = useState();
    const [cmc, setCmc] = useState();
    const [tab, setTab] = useState(0);
    const [toolbarBtns, setToolbarBtns] = useState(DEFAULT_BTNS);
    // hardcodded for now
    const [pinned] = useState({
        read: true,
        fetch: true,
        pause: true,
        resume: true,
    });

    const onTagsOpSelect = _get(state, 'handlers.onTagsOpSelect');
    const onRemoveTag = _get(state, 'handlers.onRemoveTag');
    const onRemoveGroup = _get(state, 'handlers.onRemoveGroup');
    const onAddGroup = _get(state, 'handlers.onAddGroup');
    const onClearGroups = _get(state, 'handlers.onClearGroups');
    const onTagAction = _get(state, 'handlers.onTagAction');
    const onMitreAction = _get(state, 'handlers.onMitreAction');
    const onIncidentsAction = _get(state, 'handlers.onIncidentsAction');
    const onGrouping = _get(state, 'handlers.onGrouping');
    const onColumnsSel = _get(state, 'handlers.onColumnsSel');
    const onRead = _get(state, 'handlers.onRead');
    const onPause = _get(state, 'handlers.onPause');
    const onFetch = _get(state, 'handlers.onFetch');
    const onTune = _get(state, 'handlers.onTune');
    const onShare = _get(state, 'handlers.onShare');
    const onReport = _get(state, 'handlers.onReport');
    const onViewAlerts = _get(state, 'handlers.onViewAlerts');
    const onCmcsAdd = _get(state, 'handlers.onCmcsAdd');
    const onCmcRemove = _get(state, 'handlers.onCmcRemove');
    const onCmcSelect = _get(state, 'handlers.onCmcSelect');

    const getDropTarget = () => {
        // https://github.com/mui/mui-x/pull/1872
        const source = document.querySelectorAll('.MuiDataGrid-columnHeader--moving');
        if (!source || source.length < 1) return false;
        const s = source[0].getAttribute('data-field');

        if ((groups || []).length > 2) return false;
        if (groups.find(g => g.field === s)) return false;
        return fields[s] && fields[s].indexed && fields[s].groupable !== false
            ? fields[s] : false;
    };

    const onDrop = () => {
        dropzone.current.classList.remove('dragover');
        dropzone.current.classList.remove('nodragover');
        const c = getDropTarget();
        if (!c) return;
        onAddGroup(c);
    };

    const onDragOver = e => {
        if (!dropzone.current) return;
        if (!getDropTarget()) {
            dropzone.current.classList.add('nodragover');
        } else {
            dropzone.current.classList.add('dragover');
        }
        e.preventDefault();
    };

    const onDragLeave = e => {
        if (!dropzone.current) return;
        dropzone.current.classList.remove('dragover');
        dropzone.current.classList.remove('nodragover');
        e.preventDefault();
    };

    const handleOnGrouping = () => {
        if (grouping) {
            onClearGroups();
        }
        onGrouping(!grouping);
    };

    const handleCloseCMC = (event, cmc) => {
        event.stopPropagation();
        onCmcRemove(cmc);
    };

    const handleSelectCMC = (event, tab) => {
        if ((selectedCmcs || []).length === 0) return;
        onCmcSelect(selectedCmcs[tab]);
    };

    const readIcon = useMemo(() => {
        return read === 'all' ? 'all_inclusive' : read === 'read' ? 'visibility' : 'visibility_off';
    }, [read]);

    const readTitle = useMemo(() => {
        return read === 'all' ? 'All' : read === 'read' ? 'Read' : 'Unread';
    }, [read]);

    const hideActions = useMemo(() => {
        return BRICATA_APPLIANCE_TYPE === 'mc2' && !cmc;
    }, [cmc]);

    const frequent = useCallback(btn => {
        return pinned[btn] || toolbarBtns[btn] !== undefined;
    }, [toolbarBtns, pinned]);

    const initCap = str => {
        if (!(str || '').length) return str;
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    const reportIcon = fmt => {
        switch (fmt) {
            case 'text': return <TextIcon fontSize="small" />;
            case 'csv': return <CsvIcon fontSize="small" />;
            case 'html': return <HtmlIcon fontSize="small" />;
            default: return <SaveAltIcon fontSize="small" />;
        }
    };

    const getFrequentBtns = useCallback((toolbar) => {
        return Object.entries(toolbar)
            // sort desc
            .sort(([ak, av], [bk, bv]) => bv - av)
            // 3 most used (plus pinned columns)
            .slice(0, 3)
            // back into an object
            .reduce((a, [k, v]) => ({ ...a, [k]: v }), {});
    }, []);

    const onClick = useCallback((btn, force) => {
        const profile = getProfile(state.params.profile);
        const toolbar = profile.toolbar || DEFAULT_BTNS;
        toolbar[btn] = (toolbar[btn] || 0) + 1;
        const frequent = getFrequentBtns(toolbar);
        if (force) frequent[btn] = toolbar[btn];
        setToolbarBtns(frequent);
        updateProfile(state.params.profile, { ...profile, toolbar });
    }, [getProfile, updateProfile, getFrequentBtns, state.params.profile]);

    useEffect(() => {}, [onClick, onRead]);
    const handleOnReadSelect = useCallback(sel => {
        onClick('read');
        onRead(sel);
    }, [onClick, onRead]);

    useEffect(() => {
        const profile = getProfile(state.params.profile);
        const frequent = getFrequentBtns(profile.toolbar || DEFAULT_BTNS);
        setToolbarBtns(frequent);
    }, [state.params.profile, getProfile, getFrequentBtns]);

    useEffect(() => {
        if (!cmc) return;
        const i = (selectedCmcs || []).findIndex(c => c === cmc.uuid);
        if (i < 0 || i >= (selectedCmcs || []).length) return;
        setTab(i);
    }, [cmc, selectedCmcs]);

    useEffect(() => {
        if (!state || !state.params) return;
        setGroups(state.params.groups || []);
        setTags(state.params.tagFilters || []);
        setTagsOp(state.params.tagsOp);
        setGrouping(state.params.grouping);
        setMode(state.params.mode);
        setRead(state.params.read);
        setHideGrouping(state.params.hideGrouping);
        setFields(state.params.fields);
        setPause(state.params.pause);
        setHasAlerts(state.params.hasAlerts);

        // mc2
        setCmcs(state.params.cmcs);
        setSelectedCmcs(state.params.selectedCmcs);
        setCmc(state.params.cmc);
    }, [state], true);

    const popupMenu = () => (
        <PopupState variant="popover" popupId="table-toolbar" disableAutoFocus>
            {(popupState) => (
                <div>
                    <IconButton {...bindFocus(popupState)} aria-label="more" size="large">
                        <MoreVertIcon />
                    </IconButton>
                    {/* click/blur to open/close Menu*/}
                    <CascadingMenu
                        PaperProps={{ style: { width: 175 } }}
                        popupState={popupState}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
                        {onColumnsSel && !hideActions && !frequent('columns') &&
                            <CascadingMenuItem onClick={() => { onClick('columns'); onColumnsSel() }}>
                                <ListItemIcon> <ViewWeekIcon fontSize="small" /> </ListItemIcon>
                                <ListItemText style={{ minWidth: 100 }}>Columns</ListItemText>
                                {/* <PushPinIcon onClick={() => onPin('columns')} fontSize="small"/> */}
                            </CascadingMenuItem>
                        }
                        {onGrouping && !hideActions && !frequent('groups') &&
                            <CascadingMenuItem onClick={() => { onClick('groups'); handleOnGrouping() }}>
                                <ListItemIcon> <GroupWorkIcon fontSize="small" /> </ListItemIcon>
                                <ListItemText style={{ minWidth: 100 }}>Groups</ListItemText>
                                {/* <PushPinIcon onClick={() => onPin('groups')} fontSize="small"/> */}
                            </CascadingMenuItem>
                        }
                        {onTagAction && !hideActions && !frequent('tags') &&
                            <CascadingMenuItem onClick={() => { onClick('tags'); onTagAction() }}>
                                <ListItemIcon> <BookmarkIcon fontSize="small" /> </ListItemIcon>
                                <ListItemText style={{ minWidth: 100 }}>Tags</ListItemText>
                                {/* <PushPinIcon onClick={() => onPin('tags')} fontSize="small"/> */}
                            </CascadingMenuItem>
                        }
                        {onMitreAction && !hideActions && !frequent('attack') &&
                            <CascadingMenuItem onClick={() => { onClick('attack'); onMitreAction() }}>
                                <ListItemIcon>
                                    <Icon
                                        fontSize="small"
                                        style={{
                                            fontFamily: 'unset',
                                        }}>&
                                    </Icon>
                                </ListItemIcon>
                                <ListItemText style={{ minWidth: 100 }}>ATT&CK</ListItemText>
                                {/* <PushPinIcon onClick={() => onPin('attack')} fontSize="small"/> */}
                            </CascadingMenuItem>
                        }
                        {onIncidentsAction && !hideActions && !frequent('incident') &&
                            <CascadingMenuItem onClick={() => { onClick('incident'); onIncidentsAction() }}>
                                <ListItemIcon> <WarningIcon fontSize="small" /> </ListItemIcon>
                                <ListItemText style={{ minWidth: 100 }}>Incident</ListItemText>
                                {/* <PushPinIcon onClick={() => onPin('incident')} fontSize="small"/> */}
                            </CascadingMenuItem>
                        }
                        {onViewAlerts && !hideActions && !frequent('alerts') &&
                            <CascadingMenuItem onClick={() => { onClick('alerts'); onViewAlerts() }}>
                                <ListItemIcon>
                                    <HuntIcon fontSize="small" {... hasAlerts ? { color: 'secondary' } : {}}/>
                                </ListItemIcon>
                                <ListItemText style={{ minWidth: 100 }}>Hunt</ListItemText>
                                {/* <PushPinIcon onClick={() => onPin('alerts')} fontSize="small"/> */}
                            </CascadingMenuItem>
                        }
                        {onTune && !hideActions && !frequent('tune') &&
                            <CascadingMenuItem onClick={() => { onClick('tune'); onTune() }}>
                                <ListItemIcon> <TuneIcon fontSize="small" /> </ListItemIcon>
                                <ListItemText style={{ minWidth: 100 }}>Tune</ListItemText>
                                {/* <PushPinIcon onClick={() => onPin('tune')} fontSize="small"/> */}
                            </CascadingMenuItem>
                        }
                        {onShare && !hideActions && !frequent('share') &&
                            <CascadingMenuItem onClick={() => { onClick('share'); onShare() }}>
                                <ListItemIcon> <ShareIcon fontSize="small" /> </ListItemIcon>
                                <ListItemText style={{ minWidth: 100 }}>Share</ListItemText>
                                {/* <PushPinIcon onClick={() => onPin('share')} fontSize="small"/> */}
                            </CascadingMenuItem>
                        }
                        {!pinned.fetch && onFetch && !hideActions && !frequent('fetch') &&
                            <CascadingMenuItem onClick={() => { onClick('fetch'); onFetch() }}>
                                <ListItemIcon> <CachedIcon fontSize="small" /> </ListItemIcon>
                                <ListItemText style={{ minWidth: 100 }}>Fetch</ListItemText>
                                {/* <PushPinIcon onClick={() => onPin('fetch')} fontSize="small"/> */}
                            </CascadingMenuItem>
                        }
                        {!pinned.pause && onPause && !hideActions && !frequent('pause') &&
                            <CascadingMenuItem onClick={() => { onClick('pause'); onPause() }}>
                                <ListItemIcon> <PauseIcon fontSize="small" /> </ListItemIcon>
                                <ListItemText style={{ minWidth: 100 }}>Pause</ListItemText>
                                {/* <PushPinIcon onClick={() => onPin('pause')} fontSize="small"/> */}
                            </CascadingMenuItem>
                        }
                        {!pinned.resume && onPause && !hideActions && !frequent('resume') &&
                            <CascadingMenuItem onClick={() => { onClick('resume'); onPause() }}>
                                <ListItemIcon> <PlayArrowIcon fontSize="small" /> </ListItemIcon>
                                <ListItemText style={{ minWidth: 100 }}>Resume</ListItemText>
                                {/* <PushPinIcon onClick={() => onPin('resume')} fontSize="small"/> */}
                            </CascadingMenuItem>
                        }
                        {!pinned.read && onRead && !hideActions && !frequent('read') &&
                            <CascadingLeftSubmenu popupId="table-toolbar-read" title={initCap(read)}>
                                <CascadingMenuItem onClick={() => { onClick('read'); onRead('all') }}>
                                    <ListItemIcon> <AllInclusiveIcon fontSize="small" /> </ListItemIcon>
                                    <ListItemText style={{ minWidth: 100 }}>All</ListItemText>
                                    {/* <PushPinIcon onClick={() => onPin('all')} fontSize="small"/> */}
                                </CascadingMenuItem>
                                <CascadingMenuItem onClick={() => { onClick('read'); onRead('read') }}>
                                    <ListItemIcon> <VisibilityIcon fontSize="small" /> </ListItemIcon>
                                    <ListItemText style={{ minWidth: 100 }}>Read</ListItemText>
                                    {/* <PushPinIcon onClick={() => onPin('all')} fontSize="small"/> */}
                                </CascadingMenuItem>
                                <CascadingMenuItem onClick={() => { onClick('read'); onRead('unread') }}>
                                    <ListItemIcon> <VisibilityOffIcon fontSize="small" /> </ListItemIcon>
                                    <ListItemText style={{ minWidth: 100 }}>Unread</ListItemText>
                                    {/* <PushPinIcon onClick={() => onPin('all')} fontSize="small"/> */}
                                </CascadingMenuItem>
                            </CascadingLeftSubmenu>
                        }
                        {reportPerms.write && onReport && !hideActions && !frequent('report') &&
                            <CascadingLeftSubmenu popupId="table toolbar-report" title='Report'>
                                {ExportTypes.map(t => (
                                    <CascadingMenuItem
                                        key={t.key}
                                        onClick={() => { onReport(t) }}>
                                        <ListItemIcon> {reportIcon(t.key)} </ListItemIcon>
                                        <ListItemText style={{ minWidth: 100 }}>{t.title}</ListItemText>
                                        {/* <PushPinIcon onClick={() => onPin('all')} fontSize="small"/> */}
                                    </CascadingMenuItem>
                                ))}
                            </CascadingLeftSubmenu>
                        }
                    </CascadingMenu>
                </div>
            )}
        </PopupState>
    );

    const toolbar = () => {
        return <LabeledToolbar>
            {onColumnsSel && !hideActions && frequent('columns') &&
                <Button
                    size="small"
                    startIcon={<ViewWeekIcon />}
                    onClick={() => { onClick('columns'); onColumnsSel() }}
                    title="select columns">
                    <p>Columns</p>
                </Button>
            }
            {onGrouping && !hideActions && frequent('groups') &&
                <Button
                    size="small"
                    startIcon={<GroupWorkIcon />}
                    onClick={() => { onClick('groups'); handleOnGrouping() }}
                    title={grouping ? 'clear groups' : 'group'}>
                    <p>Groups</p>
                </Button>
            }
            {tagPerms.read && onTagAction && !hideActions && frequent('tags') &&
                <Button
                    size="small"
                    startIcon={<BookmarkIcon />}
                    onClick={() => { onClick('tags'); onTagAction() }} title="tag">
                    <p>Tags</p>
                </Button>
            }
            {onMitreAction && !hideActions && frequent('attack') &&
                <Button
                    size="small"
                    startIcon={<Icon
                        fontSize="small"
                        style={{ fontFamily: 'unset', width: '100%', height: '1.44em' }}>&
                    </Icon>}
                    onClick={() => { onClick('attack'); onMitreAction() }} title="ATT&CK">
                    <p>ATT&CK</p>
                </Button>
            }
            {onIncidentsAction && !hideActions && frequent('incident') &&
                <Button
                    size="small"
                    startIcon={<WarningIcon/>}
                    onClick={() => { onClick('incident'); onIncidentsAction() }} title="incidents">
                    <p>Incident</p>
                </Button>
            }
            {onViewAlerts && !hideActions && frequent('alerts') &&
                <Button
                    size="small"
                    startIcon={<HuntIcon/>}
                    onClick={() => { onClick('alerts'); onViewAlerts() }}
                    title="Has Alert"
                    aria-label="Has Alert"
                    style={{ color: hasAlerts ? theme.palette.secondary.main : '' }}
                    {... hasAlerts ? { color: 'secondary' } : {}}>
                    <p>Hunt</p>
                </Button>
            }
            {onRead && !hideActions && frequent('read') &&
                <PopupMenu
                    tooltip="read filter"
                    items={READ_LIST}
                    selection={read}
                    onSelect={handleOnReadSelect}
                    starticon={readIcon}
                    title={readTitle}
                    placement={'bottom-end'}
                    name="read"/>
            }
            {onTune && !hideActions && frequent('tune') &&
                <Button
                    size="small"
                    startIcon={<TuneIcon/>}
                    onClick={() => { onClick('tune'); onTune() }} title="tune" aria-label="tune">
                    <p>Tune</p>
                </Button>
            }
            {reportPerms.read && onReport && !hideActions && frequent('report') &&
                <PopupMenu
                    items={ExportTypes}
                    itemkey="key"
                    itemTitle="title"
                    onSelect={onReport}
                    tooltip="select report format"
                    aria-label="report"
                    starticon='get_app'
                    title='Report'
                    placement='bottom-end'
                    name="report"/>
            }
            {onShare && !hideActions && frequent('share') && <Button
                size="small"
                startIcon={<ShareIcon/>}
                onClick={() => { onClick('share'); onShare() }} title="share" aria-label="share">
                <p>Share</p>
            </Button>}
            {onFetch && !hideActions &&
                (frequent('fetch') || frequent('pause') || frequent('resume')) &&
                <Divider orientation="vertical" className={classes.divider} flexItem />}
            {onFetch && !hideActions && frequent('fetch') &&
                <Button
                    size="small"
                    startIcon={<CachedIcon/>}
                    onClick={() => { onClick('fetch'); onFetch() }} title="fetch latest" aria-label="fetch latest">
                    <p>Fetch</p>
                </Button>
            }
            {onPause && !pause && !hideActions && frequent('pause') &&
                <Button
                    size="small"
                    startIcon={<PauseIcon/>}
                    onClick={() => { onClick('pause'); onPause() }} title="pause" aria-label="pause">
                    <p>Pause</p>
                </Button>
            }
            {onPause && pause && !hideActions && frequent('resume') &&
                <Button
                    size="small"
                    startIcon={<PlayArrowIcon/>}
                    onClick={() => { onClick('resume'); onPause() }} title="resume" aria-label="resume">
                    <p>Resume</p>
                </Button>
            }
            {!hideActions && popupMenu()}
            {BRICATA_APPLIANCE_TYPE === 'mc2' &&
                <React.Fragment>
                    <Divider orientation="vertical" className={classes.divider} flexItem />
                    <PopupMenu
                        items={Object.entries(cmcs || {})
                            .map(([uuid, name]) => ({ uuid, name }))
                            .filter(c => !(selectedCmcs || []).find(sc => sc === c.uuid))
                        }
                        itemkey="uuid"
                        itemTitle="name"
                        onSelect={onCmcsAdd}
                        tooltip="cmcs"
                        aria-label="cmcs"
                        disabled={(cmcs || []).length === (selectedCmcs || []).length}
                        button={<Button size="small" startIcon={<PollIcon/>}>
                            <p>CMC</p>
                        </Button>}
                        placement={'bottom-end'}
                        name="cmcs"/>
                </React.Fragment>
            }
        </LabeledToolbar>;
    };

    return <React.Fragment>
        <GridToolbarContainer ref={ref} spacing={1} {...props}> {mode === 'history'
            ? (<Grid container spacing={1} direction="row" justifyContent="space-between">
                <Grid item xs={8} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="h6" className={classes.content}>
                            Recently Viewed
                    </Typography>
                </Grid>
                <Grid container justifyContent="flex-end" style={{ width: 'auto' }}>
                    <Grid item>
                        {toolbar()}
                    </Grid>
                </Grid>
            </Grid>)
            : (<Grid container spacing={1} direction="row" wrap='nowrap' justifyContent="space-between">
                <Grid item>
                    <div className={classes.root}>
                        {onGrouping && grouping && !hideActions &&
                            <div className={classes.list}>
                                <div
                                    ref={dropzone}
                                    className={classes.dropzone}
                                    onDrop={onDrop}
                                    onDragOver={onDragOver}
                                    onDragLeave={onDragLeave}>
                                    <Typography variant="caption" className={classes.toolbarContent}>
                                        {(groups || []).length > 0
                                            ? '' : 'Drop headers here to apply up to 3 groups'}
                                    </Typography>
                                    {(groups || []).map((c, i) =>
                                        <Chip key={c.title}
                                            label={c.desc || c.title}
                                            onDelete={() => onRemoveGroup(c)}
                                            color="primary"
                                            variant="outlined"/>
                                    )}
                                </div>
                            </div>
                        }
                    </div>
                </Grid>
                <Grid item style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                    {onRemoveTag && tags.length > 0 && !hideActions &&
                            <PopupMenu
                                className={classes.root}
                                items={['Or', 'And', 'Not']}
                                onSelect={onTagsOpSelect}
                                icon={<div className={classes.operator}>{tagsOp}</div>}
                                size='small'
                                placement={'bottom-end'}
                                name="tags"/>
                    }
                    {onRemoveTag && !hideActions && tags.map((t, i) =>
                        <React.Fragment key={`${t}_${i}`} >
                            <Chip
                                size="small"
                                variant="outlined"
                                avatar={<Tag tag={t}/>}
                                label={t}
                                onDelete={() => onRemoveTag(t)}/>
                        </React.Fragment>
                    )}
                </Grid>
                <Grid container justifyContent="flex-end" style={{ width: 'auto' }}>
                    <Grid item>
                        {toolbar()}
                    </Grid>
                </Grid>
            </Grid>
            )}
        </GridToolbarContainer>
        {BRICATA_APPLIANCE_TYPE === 'mc2' &&
            <AppBar position="static">
                <Tabs
                    value={tab < (selectedCmcs || []).length ? tab : 0}
                    onChange={handleSelectCMC}
                    variant="scrollable"
                    scrollButtons="auto">
                    {(selectedCmcs || []).map((c, i) =>
                        <Tab
                            key={`${c}_${i}`}
                            label={
                                <span>
                                    {(cmcs || {})[c]}
                                    <a
                                        aria-label="close"
                                        className={classes.closeButton}
                                        onClick={e => handleCloseCMC(e, c)}>
                                        <CloseIcon fontSize="small"/>
                                    </a>
                                </span>}/>
                    )}
                </Tabs>
            </AppBar>
        }
    </React.Fragment>;
});
TableToolbar.displayName = 'TableToolbar';

export {
    TableToolbar as default,
    ListMode,
    DetailMode,
    HistoryMode,
    FivetupleMode,
    ThreetupleMode,
    MapMode,
};
